
import React from 'react';
import { Edit, Trash, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CountryFlag from '@/components/country/CountryFlag';
import { CountryItem } from '../types';
import { CountryStatus } from './CountryStatus';
import { HeroImageStatus } from './HeroImageStatus';
import { useRegions } from '@/hooks/useRegions';

interface CountryRowProps {
  country: CountryItem;
  editing: boolean;
  editData: CountryItem | null;
  onEditDataChange: (data: CountryItem) => void;
  onSave: () => void;
  onDelete: (id: string) => void;
  onOpenDialog: (country: CountryItem) => void;
  onToggleActive: (id: string) => void;
}

const CountryRow: React.FC<CountryRowProps> = ({
  country,
  editing,
  editData,
  onEditDataChange,
  onSave,
  onDelete,
  onOpenDialog,
  onToggleActive,
}) => {
  const { regions } = useRegions();

  return (
    <tr key={country.code}>
      <td className="p-4">
        <CountryFlag countryCode={country.code} className="h-5 w-6" />
      </td>
      <td className="p-4 font-medium">
        {editing ? (
          <Input
            value={editData?.name || ''}
            onChange={(e) => onEditDataChange({ ...editData!, name: e.target.value })}
            className="w-full"
          />
        ) : (
          country.name
        )}
      </td>
      <td className="p-4">
        {editing ? (
          <Input
            value={editData?.code || ''}
            onChange={(e) => onEditDataChange({ ...editData!, code: e.target.value.toUpperCase() })}
            className="w-20"
            maxLength={2}
          />
        ) : (
          country.code
        )}
      </td>
      <td className="p-4">
        {editing ? (
          <Select 
            value={editData?.region_id || "none"} 
            onValueChange={(value) => onEditDataChange({ ...editData!, region_id: value === "none" ? undefined : value })}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Region</SelectItem>
              {regions
                .filter(region => region.active)
                .map((region) => (
                  <SelectItem key={region.id} value={region.id}>
                    {region.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-sm text-muted-foreground">
            {country.region_name || 'No Region'}
          </span>
        )}
      </td>
      <td className="p-4">
        <CountryStatus 
          active={country.active} 
          onToggle={() => onToggleActive(country.id)} 
        />
      </td>
      <td className="p-4">
        <HeroImageStatus imageUrl={country.background_image_url} />
      </td>
      <td className="p-4 text-right">
        {editing ? (
          <Button size="sm" onClick={onSave}>
            <Save size={16} />
          </Button>
        ) : (
          <div className="flex justify-end space-x-2">
            <Button variant="outline" size="sm" onClick={() => onOpenDialog(country)}>
              <Edit size={16} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => onDelete(country.id)}>
              <Trash size={16} />
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
};

export default CountryRow;
